/**
 * 主题主色：构建时由 REACT_APP_THEME_PRIMARY（#RRGGBB）配置，运行时在入口最早调用 applyThemeCssVars() 注入 CSS 变量。
 * 与 public/css/theme.css 中的 --falcon-* 对齐，并增加 --app-theme-* 供内联样式与渐变使用。
 */

const DEFAULT_PRIMARY = '#009be2';

/**
 * 浅色模式是否再压暗品牌色（向黑混合）。
 * 曾用 0.08 抬白底对比；保持 0 用配置原色。
 */
const LIGHT_BRAND_DEEPEN = 0;

function normalizeHex(input) {
	if (!input || typeof input !== 'string') return null;
	let h = input.trim();
	if (!h.startsWith('#')) h = `#${h}`;
	if (!/^#[0-9a-fA-F]{6}$/.test(h)) return null;
	return h.toLowerCase();
}

/**
 * @returns {string} 规范化后的主色 hex
 */
export function getResolvedThemePrimary() {
	return normalizeHex(process.env.REACT_APP_THEME_PRIMARY) || DEFAULT_PRIMARY;
}

export function hexToRgbTriplet(hex) {
	const h = normalizeHex(hex);
	if (!h) return null;
	const n = parseInt(h.slice(1), 16);
	return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbTripletToHex(r, g, b) {
	const to = (x) => x.toString(16).padStart(2, '0');
	return `#${to(r)}${to(g)}${to(b)}`;
}

/** t: 0 → a, 1 → b */
export function mixHex(a, b, t) {
	const ra = hexToRgbTriplet(a);
	const rb = hexToRgbTriplet(b);
	if (!ra || !rb) return getResolvedThemePrimary();
	const r = Math.round(ra[0] + (rb[0] - ra[0]) * t);
	const g = Math.round(ra[1] + (rb[1] - ra[1]) * t);
	const bl = Math.round(ra[2] + (rb[2] - ra[2]) * t);
	return rgbTripletToHex(Math.min(255, Math.max(0, r)), Math.min(255, Math.max(0, g)), Math.min(255, Math.max(0, bl)));
}

/**
 * @param {boolean} isLight 当前是否为浅色模式
 * @returns {string} 该模式下用于 UI 的品牌主色
 */
export function getThemePrimaryForMode(isLight) {
	const base = getResolvedThemePrimary();
	return isLight ? mixHex(base, '#000000', LIGHT_BRAND_DEEPEN) : base;
}

function resolveIsLight(options = {}) {
	if (typeof options.isLight === 'boolean') return options.isLight;
	if (typeof document === 'undefined') return true;
	return document.documentElement.getAttribute('data-bs-theme') !== 'dark';
}

/**
 * 将解析后的主色写入 document.documentElement，覆盖 theme.css 中的默认值。
 * 须在首屏渲染前调用一次（见 themePaletteInit.js）；切换深浅色时由 AppProvider 再次调用。
 */
export function applyThemeCssVars(options = {}) {
	if (typeof document === 'undefined') return;

	const isLight = resolveIsLight(options);
	const primary = getThemePrimaryForMode(isLight);
	const rgb = hexToRgbTriplet(primary);
	if (!rgb) return;

	const rgbStr = `${rgb[0]}, ${rgb[1]}, ${rgb[2]}`;
	const hover = mixHex(primary, '#000000', 0.22);
	const light = mixHex(primary, '#ffffff', isLight ? 0.22 : 0.28);
	const hoverRgb = hexToRgbTriplet(hover);
	const lightRgb = hexToRgbTriplet(light);
	const hoverRgbStr = hoverRgb ? `${hoverRgb[0]}, ${hoverRgb[1]}, ${hoverRgb[2]}` : rgbStr;
	const lightRgbStr = lightRgb ? `${lightRgb[0]}, ${lightRgb[1]}, ${lightRgb[2]}` : rgbStr;

	const root = document.documentElement;
	root.style.setProperty('--falcon-primary', primary);
	root.style.setProperty('--falcon-primary-rgb', rgbStr);
	root.style.setProperty('--falcon-link-color', primary);
	root.style.setProperty('--falcon-link-color-rgb', rgbStr);
	root.style.setProperty('--falcon-link-hover-color', hover);
	root.style.setProperty('--falcon-link-hover-color-rgb', hoverRgbStr);

	// Bootstrap 5 部分组件使用 --bs-primary
	root.style.setProperty('--bs-primary', primary);
	root.style.setProperty('--bs-primary-rgb', rgbStr);

	// 应用内联样式、渐变（不依赖编译期 SCSS）
	root.style.setProperty('--app-theme-primary', primary);
	root.style.setProperty('--app-theme-primary-rgb', rgbStr);
	root.style.setProperty('--app-theme-primary-hover', hover);
	root.style.setProperty('--app-theme-primary-hover-rgb', hoverRgbStr);
	root.style.setProperty('--app-theme-primary-light', light);
	root.style.setProperty('--app-theme-primary-light-rgb', lightRgbStr);
}
